import style from './WalletPage.css';
import TransactionsList from '../../components/Wallet/TransactionsList/TransactionsList';
import DebitCard from '../../components/shared/DebitCard/DebitCard';
import IconButton from '../../components/shared/IconButton/IconButton';
import ArrowUpIcon from '../../components/shared/Icons/ArrowUpIcon';
import useTranslation from '../../localization/i18n.context';
import { useEffect, useRef, useState } from 'preact/hooks';
import { CloseWebviewEvent } from '../../sdk/close.webview.event';
import { renderIf } from '../../utils/render.utils';
import LoadingScreen from '../../components/shared/LoadingScreen/LoadingScreen';
import AppBar from '../../components/shared/AppBar/AppBar';
import EmptyTransactionList from '../../components/Wallet/EmptyTransactionList/EmptyTransactionList';
import useConfig from '../../config/config.context';
import { _className } from '../../utils/classname.utils';
import BalanceControl from '../../components/Wallet/BalanceControl/BalanceControl';
import CardMessage from '../../components/Wallet/CardStatus/CardMessage';
import ArrayUtils from '../../utils/array.utils';
import FormatterUtils from '../../utils/formatter.utils';
import useIntersection from '../../hooks/intersection.hook';
import useAndroidBackPress from '../../hooks/back.listener.hook';
import NavigationUtils from '../../utils/navigation.utils';
import CardService from '../../services/card.service';
import TransactionService from '../../services/transaction.service';
import CaasStorageKeys from '../../constants/caas.storage.keys';
import SettingsService from '../../services/settings.service';
import CardStatus from '../../constants/card.status';
import ArrowRightIcon from '../../components/shared/Icons/ArrowRightIcon';
import SettingsBarIcon from '../../components/shared/Icons/SettingsBarIcon';
import ArrowUpDownIcon from '../../components/shared/Icons/ArrowUpDownIcon';
import Tooltip from '../../components/shared/Tooltip/Tooltip';
import CreditCardBox from '../../components/Wallet/CreditCard/CreditCardBox';
import Carousel from '../../components/shared/Carousel/Carousel';
import CardMessagesList from '../../components/Wallet/CardStatus/CardMessageList';
import RefreshIndicator from '../../components/shared/RefreshIndicator/RefreshIndicator'; // Importa el componente de refresco
import usePullToRefresh from '../../hooks/pull.refresh.hook';

import {
  CreditCardAvailable,
  CreditDisabled,
  UseCreditButtonEnabled,
} from '../../components/Wallet/CreditCard/CreditConfig';
import { EnterAmountType } from '../EnterAmountPage/EnterAmountProvider';
import { ConsoleEvent } from '../../sdk/console.event';
import CreditStatus from '../../constants/credit.status';
import PosBottomSheet from '../../components/Pos/PosBottomSheet';
import PaymentStatus from '../../constants/payment.status';
import SendMoneyBottomSheet from '../../components/SendMoney/SendMoneyBottomSheet';

export default function WalletPage({ organization: organizationCode }) {
  const backRef = useRef(null);
  const loadMoreRef = useRef(null);

  const { t, getOrganization, getCurrency } = useTranslation();
  const { config, updateStorage } = useConfig();
  const { isIntersecting } = useIntersection(loadMoreRef.current);

  const [loadingScreen, setLoadingScreen] = useState(true);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [card, setCard] = useState({});
  const [credit, setCredit] = useState({});
  const [transactions, setTransactions] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMoreTransactions, setHasMoreTransactions] = useState(true);
  const [sendMoneyButtonEnabled, setSendMoneyButtonEnabled] = useState(false);
  const [sendMoneyToCompanyEnabled, setSendMoneyToCompanyEnabled] =
    useState(false);
  const [useCreditButtonEnabled, setUseCreditButtonEnabled] = useState(false);
  const [posOptionsButtonEnabled, setPosOptionsButtonEnabled] = useState(false);
  const [cardMessageItems, setCardMessageItems] = useState([]);
  const [showPosBottomSheet, setShowPosBottomSheet] = useState(false);
  const [showSendMoneyBottomSheet, setShowSendMoneyBottomSheet] =
    useState(false);
  const [sendMoneyOptionsButtonEnabled, setSendMoneyOptionsButtonEnabled] =
    useState(false);

  const reloadTransactions = () => {
    if (!card.uid || refreshing) return Promise.resolve();
    return TransactionService.get(card.uid, organizationCode, 1).then(
      _transactions => {
        setTransactions(
          ArrayUtils.groupTransactions(_transactions.data, getCurrency),
        );
        setPage(2);
        setHasMoreTransactions(_transactions.pagination.hasNext);
      },
    );
  };

  //Hook to handle "pull to refresh"
  const {
    refreshing,
    refreshHeight,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  } = usePullToRefresh(reloadTransactions);

  const handleBackClick = () => {
    CloseWebviewEvent.closedByUser();
  };

  const loadMore = () => {
    if (!card.uid) {
      return;
    }
    setLoadingTransactions(true);

    TransactionService.get(card.uid, organizationCode, page).then(
      _transactions => {
        setLoadingTransactions(false);
        setTransactions(
          ArrayUtils.mergeTransactions(
            transactions,
            ArrayUtils.groupTransactions(_transactions.data, getCurrency),
          ),
        );
        setPage(prev => prev + 1);

        setHasMoreTransactions(_transactions.pagination.hasNext);
      },
    );
  };

  useEffect(() => {
    if (
      isIntersecting &&
      !loadingScreen &&
      hasMoreTransactions &&
      !loadingTransactions
    ) {
      ConsoleEvent.log('Loading more transactions');
      loadMore();
    }
  }, [isIntersecting]);

  useEffect(() => {
    return () => {
      if (!config[CaasStorageKeys.STORAGE].hasTooltipBeenShown) {
        updateStorage('hasCreditTooltipBeenShown', true);
      }
    };
  }, []);

  useEffect(() => {
    let _card = null;
    let _credit = null;
    let _cardMessageItems = [];
    let _lendingButtonEnabled = false;
    let _sendMoneyButtonEnabled = false;
    let _topUpOptionsButtonEnabled = false;
    let _sendMoneyOptionsButtonEnabled = false;
    let _sendMoneyToCompanyEnabled = false;

    SettingsService.getWallet()
      .then(settingsResponse => {
        const { sendMoneyButtonEnabled, lendingButtonEnabled } =
          settingsResponse.data;

        _sendMoneyButtonEnabled = sendMoneyButtonEnabled;
        _lendingButtonEnabled = lendingButtonEnabled;

        return CardService.get(organizationCode);
      })
      .then(response => {
        /* ATTENTION: when the organization code filter is applied the endpoints return at least one card
         */
        _card = response.data?.filter(
          item => item.organizationCode === organizationCode,
        )?.[0];
        _credit = _card.credit;
        _topUpOptionsButtonEnabled = _card.posEnabled;
        _sendMoneyOptionsButtonEnabled = _card.sendMoneyCompanyEnabled;
        _sendMoneyToCompanyEnabled = _card.sendMoneyCompanyEnabled;
        _cardMessageItems = CardMessagesList({
          status: _card.status,
          cardUid: _card.uid,
          cardNumber: _card.number,
          credit: _card?.credit,
          size: CardMessage.Size.LARGE,
          lendingButtonEnabled: _lendingButtonEnabled,
          isPersonalizedCard: _card?.personalizedCardEnabled,
          automaticLendingEnabled: _card?.automaticLendingEnabled,
        });
        if (_card == null) {
          throw new Error(
            `You do not have a card for the organization ${organizationCode}`,
          );
        }

        setCard(_card);
        setCredit(_credit);
        setCardMessageItems(_cardMessageItems);

        setSendMoneyButtonEnabled(
          _sendMoneyButtonEnabled &&
            [CardStatus.ACTIVE, CardStatus.LOCKED].includes(_card.status),
        );
        setSendMoneyToCompanyEnabled(_sendMoneyToCompanyEnabled);
        setUseCreditButtonEnabled(
          _lendingButtonEnabled &&
            _credit != null &&
            UseCreditButtonEnabled.includes(_credit.status),
        );

        setPosOptionsButtonEnabled(_topUpOptionsButtonEnabled);
        setSendMoneyOptionsButtonEnabled(_sendMoneyButtonEnabled);

        if (_card.uid) {
          return TransactionService.get(_card.uid, organizationCode, page);
        }
      })
      .then(response => {
        if (response) {
          setTransactions(
            ArrayUtils.groupTransactions(response.data, getCurrency),
          );

          setPage(prev => prev + 1);
          setHasMoreTransactions(response.pagination.hasNext);
        }

        setLoadingScreen(false);
      })
      .catch(er => {
        console.error(`Something went wrong during wallet data fetching`, er);
        NavigationUtils.navigate(NavigationUtils.Routes.TRY_AGAIN);
      });
  }, []);
  useAndroidBackPress(backRef.current, () => {
    backRef.current.props.onClick();
  });

  function onTopUpButtonClick() {
    if (posOptionsButtonEnabled) {
      setShowPosBottomSheet(true);
    } else {
      NavigationUtils.navigate(NavigationUtils.Routes.TOPUP, false, {
        source: NavigationUtils.Sources.WALLET,
      });
    }
  }

  function onSendMoneyButtonClick() {
    if (sendMoneyOptionsButtonEnabled && sendMoneyToCompanyEnabled) {
      setShowSendMoneyBottomSheet(true);
    } else {
      !shouldShowSettings() &&
        NavigationUtils.navigate(
          `${NavigationUtils.Routes.P2P_CONTACT_LIST.replace(
            ':cardUid',
            card.uid,
          )}`,
          false,
          {
            source: NavigationUtils.Sources.WALLET,
          },
        );
    }
  }

  function shouldShowSettings() {
    if (card.status === CardStatus.LOCKED) {
      NavigationUtils.navigate(NavigationUtils.Routes.CARD_SETTINGS, false, {
        cardUid: card.uid,
      });
      return true;
    }
    return false;
  }

  return (
    <>
      <AppBar
        onBackClick={handleBackClick}
        className="fixed-bar"
        title={getOrganization(card.organizationCode)}
        backRef={backRef}
        customActions={[
          <BalanceControl />,
          <SettingsBarIcon
            data-testid="settings-bar-icon"
            onClick={() => {
              NavigationUtils.navigate(
                NavigationUtils.Routes.CARD_SETTINGS,
                false,
                {
                  source: NavigationUtils.Sources.WALLET,
                  cardUid: card.uid,
                },
              );
            }}
          />,
        ]}
      />
      <RefreshIndicator height={refreshHeight} refreshing={refreshing} />
      <div
        className={style.container}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className={style.balance}>
          <small>{t('Current Balance')}</small>
          <h1>
            <span
              className={_className(
                !config[CaasStorageKeys.STORAGE].showBalance,
                'text-blur',
              )}
            >
              {FormatterUtils.currency(card.balance, getCurrency(card.currency))
                .split(' ')
                .map(el => {
                  return <span> {el}</span>;
                })}
            </span>
          </h1>
        </div>
        <DebitCard card={card} />
        {renderIf(
          cardMessageItems.length > 0,
          <div className={style.carousel}>
            <Carousel items={cardMessageItems} />
          </div>,
        )}
        <div className={style.actions}>
          <IconButton
            icon={<ArrowUpIcon />}
            text={t('Top up')}
            onClick={onTopUpButtonClick}
          />
          {renderIf(
            useCreditButtonEnabled,
            <Tooltip
              content={t(
                'New feature! Spend your credit line like cash by moving up to {available amount} from your credit line to your current account anytime.',
                {
                  '{available amount}': FormatterUtils.currency(
                    credit?.availableAmount,
                    getCurrency(credit?.currency),
                  ),
                },
              )}
              showing={
                !config[CaasStorageKeys.STORAGE].hasCreditTooltipBeenShown &&
                !CreditDisabled.includes(credit?.status)
              }
            >
              <IconButton
                icon={<ArrowUpDownIcon />}
                text={t('Use credit')}
                onClick={() => {
                  return (
                    !shouldShowSettings() &&
                    NavigationUtils.navigate(
                      NavigationUtils.Routes.ENTER_AMOUNT,
                      false,
                      {
                        type: EnterAmountType.MICROLOAN,
                        organizationCode: card.organizationCode,
                        source: NavigationUtils.Sources.WALLET,
                      },
                    )
                  );
                }}
                disabled={
                  CreditDisabled.includes(credit?.status) ||
                  ![CardStatus.ACTIVE].includes(card.status) ||
                  PaymentStatus.OVERDUE.includes(credit?.payment?.status)
                }
              />
            </Tooltip>,
          )}

          <IconButton
            icon={<ArrowRightIcon />}
            text={t('Send money')}
            disabled={!sendMoneyButtonEnabled}
            onClick={onSendMoneyButtonClick}
          />
        </div>
        {renderIf(
          (useCreditButtonEnabled &&
            credit &&
            CreditCardAvailable.includes(credit?.status)) ||
            (!useCreditButtonEnabled &&
              credit &&
              ![
                CreditStatus.PENDING_APPROVAL,
                CreditStatus.APPROVED,
                CreditStatus.ACTIVE,
                CreditStatus.NOT_APPLIED,
              ].includes(credit?.status)),
          <CreditCardBox
            className={style['credit-card']}
            credit={credit}
            payment={credit?.payment}
            cardUid={card.uid}
            cardStatus={card.status}
            organization={organizationCode}
          />,
        )}
        {renderIf(
          !transactions.length,
          <EmptyTransactionList showSubtitle={card.balance === 0} />,
        )}
        <div className={style['transactions-list']}>
          {transactions.map((el, idx) => (
            <TransactionsList
              title={el.date}
              transactions={el.transactions}
              loading={loadingTransactions && idx === transactions.length - 1}
              key={idx}
            />
          ))}
        </div>
      </div>
      <div id="loading-trigger" ref={loadMoreRef} />
      {renderIf(loadingScreen, <LoadingScreen />)}
      {renderIf(
        showPosBottomSheet && posOptionsButtonEnabled,
        <PosBottomSheet
          show={showPosBottomSheet}
          organizationCode={card.organizationCode}
          cardStatus={card.status}
          cardUid={card.uid}
        ></PosBottomSheet>,
      )}
      {renderIf(
        showSendMoneyBottomSheet && sendMoneyOptionsButtonEnabled,
        <SendMoneyBottomSheet
          show={showSendMoneyBottomSheet}
          organizationCode={card.organizationCode}
          cardStatus={card.status}
          cardUid={card.uid}
          sendMoneyToCompanyEnabled={sendMoneyToCompanyEnabled}
          onClose={() => setShowSendMoneyBottomSheet(false)}
        />,
      )}
    </>
  );
}
